import { TIMEFRAME_MS, formulaLeaves, formulaOrder, isConditionGroup, operandOffset } from "@strat/format";
import { timeframeFit } from "./timeframe.js";
/**
 * How many bars of history an indicator needs before its value is trustworthy.
 *
 * Two different situations, and conflating them is how a bot quietly starts
 * trading on half-formed signals.
 *
 * **Windowed** indicators (SMA, Bollinger) are exact as soon as they have their
 * window. A 200-period SMA needs exactly 200 bars and the 200th value is real.
 *
 * **Recursive** indicators (EMA, RSI, ATR, ADX, MACD) are seeded from a short
 * average and then updated by a decay factor, so the first values are dominated
 * by that arbitrary seed and converge on the truth only over time. An EMA(50)
 * produces a number at bar 50, but it is materially wrong until roughly 3x the
 * period has passed. Charting libraries hide this; a bot that trades on it does
 * not.
 *
 * The multiplier is the conventional rule of thumb rather than a derived
 * constant — the decay is asymptotic, so there is no exact bar at which an EMA
 * becomes "correct". Three periods puts the seed's weight under ~5%.
 */
const RECURSIVE_MULTIPLIER = 3;
/** Never trade on fewer than this, however short the windows are. */
const MINIMUM_WARMUP_BARS = 30;
/** Bars needed for one indicator, given its own parameters. */
const DAY_MS = 86_400_000;
/**
 * Bars in one session when nothing says how long a bar is: a day of 5-minute
 * bars, enough for a session VWAP on every timeframe from 5m up.
 */
const DEFAULT_SESSION_BARS = 288;
/**
 * Bars needed for one indicator, given its own parameters — and, for the one
 * indicator whose history is measured in time rather than bars (a session
 * VWAP, which reaches back to midnight), the bar length in milliseconds.
 */
export function warmupBarsFor(def, barMs) {
    // On a slower chart: its own warm-up in *slower* bars, converted to run bars,
    // plus one slower bar for the partial one a buffer starts inside (it is
    // dropped, see `resample`). An EMA(50) on 1h under a 5m run is 150 hours.
    if (def.timeframe !== undefined && timeframeFit(def.timeframe, barMs) === "higher") {
        const slowerMs = TIMEFRAME_MS[def.timeframe];
        const slowerBars = warmupBarsFor({ kind: def.kind, params: def.params }, slowerMs);
        return (slowerBars + 1) * Math.round(slowerMs / barMs);
    }
    // A formula needs what it reads, which `startupBarsFor` adds; on its own,
    // only how far back it looks.
    if (def.kind === "formula") {
        return def.formula ? Math.max(0, ...formulaLeaves(def.formula).map(operandOffset)) : 0;
    }
    const p = def.params ?? {};
    const period = p.period ?? 14;
    switch (def.kind) {
        // Exact once the window is full.
        case "sma":
        case "bbands":
            return period;
        case "stoch":
            return period + (p.signalPeriod ?? 3);
        // Recursive — seeded then decayed, so they need room to converge.
        case "ema":
        case "rsi":
        case "atr":
            return period * RECURSIVE_MULTIPLIER;
        // ADX smooths DX, which is itself built from period-length DI averages, so
        // it needs two windows before it produces anything at all.
        case "adx":
            return period * 2 * RECURSIVE_MULTIPLIER;
        case "macd":
            return ((p.slowPeriod ?? 26) + (p.signalPeriod ?? 9)) * RECURSIVE_MULTIPLIER;
        // Windowed, exact once full.
        case "wma":
        case "lsma":
        case "alma":
        case "envelope":
        case "donchian":
            return period;
        // A WMA of WMAs: the outer window starts once the inner one is full.
        case "hma":
            return period + Math.floor(Math.sqrt(period));
        case "ichimoku":
            return (Math.max(p.conversionPeriod ?? 9, p.basePeriod ?? 26, p.spanBPeriod ?? 52) +
                (p.displacement ?? 26) -
                1);
        // Recursive.
        case "smma":
        case "mcginley":
            return period * RECURSIVE_MULTIPLIER;
        // Each EMA stacked on another carries the first one's seed a period further.
        case "dema":
            return period * (RECURSIVE_MULTIPLIER + 1);
        case "tema":
            return period * (RECURSIVE_MULTIPLIER + 2);
        // Its slowest smoothing is a 30-bar EMA, whatever the efficiency period.
        case "kama":
            return period + 30 * RECURSIVE_MULTIPLIER;
        case "keltner":
            return Math.max(period, p.atrPeriod ?? 10) * RECURSIVE_MULTIPLIER;
        // Path-dependent: their state resets at each reversal, and agrees with a
        // longer history's from the first reversal both have seen. A hundred bars
        // holds several on any timeframe that trends at all.
        case "psar":
            return 100;
        case "supertrend":
            return Math.max(period * RECURSIVE_MULTIPLIER, 100);
        // --- Oscillators and volatility -------------------------------------
        // Windowed over the close, or over the previous close too (+1).
        case "cci":
        case "willr":
        case "stdev":
        case "bbpct":
        case "bbw":
        case "chop":
            return period;
        case "roc":
        case "mom":
        case "cmo":
        case "aroon":
        case "vortex":
        case "hv":
            return period + 1;
        case "dpo":
            return period + Math.floor(period / 2) + 1;
        case "ao":
            return Math.max(p.fastPeriod ?? 5, p.slowPeriod ?? 34);
        case "uo":
            return Math.max(p.fastPeriod ?? 7, p.middlePeriod ?? 14, p.slowPeriod ?? 28) + 1;
        case "coppock":
            return Math.max(p.longRoc ?? 14, p.shortRoc ?? 11) + period;
        // Recursive: the 3× rule for each smoothing in the chain.
        case "stochrsi":
            return ((p.rsiPeriod ?? 14) * RECURSIVE_MULTIPLIER +
                (p.stochPeriod ?? 14) +
                (p.smoothK ?? 3) +
                (p.smoothD ?? 3));
        case "trix":
            return period * (RECURSIVE_MULTIPLIER + 2) + 1;
        case "ppo":
            return ((p.slowPeriod ?? 26) + (p.signalPeriod ?? 9)) * RECURSIVE_MULTIPLIER;
        case "tsi":
            return ((p.longPeriod ?? 25) + (p.shortPeriod ?? 13) + (p.signalPeriod ?? 13)) * RECURSIVE_MULTIPLIER;
        // Its state decays by a third each bar, so thirty bars bury any seed.
        case "fisher":
            return period + 30;
        case "crsi":
            return Math.max((p.rsiPeriod ?? 3) * RECURSIVE_MULTIPLIER, (p.streakPeriod ?? 2) * RECURSIVE_MULTIPLIER, (p.rankPeriod ?? 100) + 1);
        case "massi":
            return 9 * 2 * RECURSIVE_MULTIPLIER + period;
        // --- Volume -----------------------------------------------------------
        // Reaches back to 00:00 UTC: a day of bars, whatever the timeframe.
        case "vwap":
            return barMs && barMs > 0 ? Math.max(1, Math.ceil(DAY_MS / barMs)) : DEFAULT_SESSION_BARS;
        case "volume":
        case "vwma":
        case "cmf":
            return period;
        case "obv":
        case "mfi":
        case "eom":
            return period + 1;
        // Running totals: their level depends on where the history starts, so
        // only their movement, and their distance from their MA, mean anything.
        case "ad":
        case "pvt":
            return period;
        case "bop":
            return 1;
        case "adosc":
        case "vo":
            return Math.max(p.fastPeriod ?? 5, p.slowPeriod ?? 10) * RECURSIVE_MULTIPLIER;
        case "efi":
            return period * RECURSIVE_MULTIPLIER + 1;
        case "rvi":
            return period + 6;
        default:
            return period * RECURSIVE_MULTIPLIER;
    }
}
/**
 * Bars of history a whole strategy needs before its first tradeable bar.
 *
 * The maximum rather than the sum: indicators are computed in parallel over the
 * same series, so the slowest one sets the requirement.
 */
export function startupBarsFor(indicators, barMs, 
/**
 * The rules, so a look-back counts: `EMA(200) 50 bars ago` is only warm
 * once the EMA has been warm for 50 bars. Omitted, only the indicators.
 */
rules = []) {
    return Math.max(MINIMUM_WARMUP_BARS, lookbackBarsFor(indicators, barMs, rules));
}
/**
 * The history the indicators and rules need, without the floor
 * `startupBarsFor` puts under it — 0 for a strategy with nothing to warm.
 * What a backtest loads in front of its first traded bar.
 */
export function lookbackBarsFor(indicators, barMs, rules = []) {
    // Per indicator: its own warm-up, and for a formula the warm-up of what it
    // reads plus how far back it reads it.
    const need = new Map();
    for (const def of indicators) {
        if (def?.kind !== "formula")
            need.set(def?.id, warmupBarsFor(def, barMs));
    }
    const leafNeed = (leaf) => {
        const offset = operandOffset(leaf);
        if (leaf.kind === "indicator")
            return (need.get(leaf.ref) ?? 0) + offset;
        if (leaf.kind === "price")
            return offset + 1;
        return 0;
    };
    for (const def of formulaOrder(indicators)) {
        need.set(def.id, def.formula ? Math.max(0, ...formulaLeaves(def.formula).map(leafNeed)) : 0);
    }
    let max = 0;
    for (const bars of need.values())
        if (bars > max)
            max = bars;
    const walk = (node, depth) => {
        if (!node || typeof node !== "object" || depth > 20)
            return;
        if (isConditionGroup(node))
            return (node.children ?? []).forEach((c) => walk(c, depth + 1));
        for (const side of [node.left, node.right]) {
            // +1 because a crossing reads the bar before as well.
            if (side && typeof side === "object")
                max = Math.max(max, leafNeed(side) + (operandOffset(side) > 0 ? 1 : 0));
        }
    };
    for (const rule of rules)
        walk(rule, 0);
    return max;
}
/** Bars a whole strategy needs before its first decision: indicators and rules. */
export function specStartupBars(spec, barMs) {
    return startupBarsFor(spec.indicators ?? [], barMs, [spec.entry, spec.exit]);
}
//# sourceMappingURL=warmup.js.map